import { X, Package, Truck, MapPin, Clock, ExternalLink, CheckCircle, AlertCircle } from 'lucide-react';
import { useEffect, useState } from 'react';

interface TrackingEvent {
  timestamp: string;
  status: string;
  location: string;
}

interface TrackingInfo {
  carrier: string;
  tracking_number: string;
  status: string;
  current_location: string;
  estimated_delivery?: string;
  estimated_delivery_end?: string;
  delivered_at?: string;
  tracking_url?: string;
  delay_reason?: string;
  events?: TrackingEvent[];
}

interface OrderItem {
  product_id: string;
  name: string;
  quantity: number;
  price: number;
}

interface TrackingCardProps {
  orderId: string;
  tracking: TrackingInfo;
  items: OrderItem[];
  onDismiss: () => void;
  autoDismissMs?: number;
}

const statusConfig: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  pending: { label: 'In behandeling', color: 'text-gray-500 bg-gray-100', icon: <Clock className="w-4 h-4" /> },
  processing: { label: 'Wordt klaargemaakt', color: 'text-blue-600 bg-blue-100', icon: <Package className="w-4 h-4" /> },
  shipped: { label: 'Verzonden', color: 'text-blue-600 bg-blue-100', icon: <Package className="w-4 h-4" /> },
  in_transit: { label: 'Onderweg', color: 'text-amber-600 bg-amber-100', icon: <Truck className="w-4 h-4" /> },
  out_for_delivery: { label: 'Wordt vandaag bezorgd', color: 'text-green-600 bg-green-100', icon: <Truck className="w-4 h-4" /> },
  delivered: { label: 'Bezorgd', color: 'text-green-700 bg-green-100', icon: <CheckCircle className="w-4 h-4" /> },
  delayed: { label: 'Vertraagd', color: 'text-red-600 bg-red-100', icon: <AlertCircle className="w-4 h-4" /> },
};

export default function TrackingCard({
  orderId,
  tracking,
  items,
  onDismiss,
  autoDismissMs = 15000,
}: TrackingCardProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    // Fade in
    setTimeout(() => setIsVisible(true), 50);

    // Progress bar countdown
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / autoDismissMs) * 100);
      setProgress(remaining);

      if (remaining <= 0) {
        clearInterval(interval);
        handleDismiss();
      }
    }, 100);

    return () => clearInterval(interval);
  }, [autoDismissMs]);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(onDismiss, 300);
  };

  const status = statusConfig[tracking.status] || statusConfig.pending;
  const itemsSummary = items.slice(0, 2).map((i) => i.name).join(', ');

  const formatDeliveryTime = () => {
    if (tracking.status === 'delivered' && tracking.delivered_at) {
      try {
        const dt = new Date(tracking.delivered_at);
        return `Bezorgd op ${dt.toLocaleDateString('nl-NL')} om ${dt.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`;
      } catch {
        return 'Bezorgd';
      }
    }

    if (!tracking.estimated_delivery) return null;

    try {
      const start = new Date(tracking.estimated_delivery);
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      let dayStr = start.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
      if (start.toDateString() === today.toDateString()) {
        dayStr = 'Vandaag';
      } else if (start.toDateString() === tomorrow.toDateString()) {
        dayStr = 'Morgen';
      }

      const startTime = start.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });

      if (tracking.estimated_delivery_end) {
        const end = new Date(tracking.estimated_delivery_end);
        const endTime = end.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
        return `${dayStr} ${startTime} - ${endTime}`;
      }

      return `${dayStr} vanaf ${startTime}`;
    } catch {
      return null;
    }
  };

  const deliveryTime = formatDeliveryTime();

  return (
    <div
      className={`fixed bottom-24 right-4 z-50 w-80 max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-900 border border-primary/20 rounded-xl shadow-2xl shadow-black/20 transition-all duration-300 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
    >
      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-primary/10 rounded-t-xl overflow-hidden">
        <div
          className="h-full bg-primary transition-all duration-100 ease-linear"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Header */}
      <div className="flex items-center justify-between p-4 pb-2">
        <div className="flex items-center gap-2">
          <Package className="w-5 h-5 text-primary" />
          <span className="font-semibold text-sm">{orderId}</span>
        </div>
        <button
          onClick={handleDismiss}
          className="p-1 hover:bg-primary/10 rounded-full transition-colors"
          aria-label="Sluiten"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>

      {/* Items */}
      <div className="px-4 pb-2">
        <p className="text-sm text-muted-foreground truncate">{itemsSummary}</p>
      </div>

      {/* Status badge */}
      <div className="px-4 pb-3">
        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium ${status.color}`}>
          {status.icon}
          {status.label}
        </div>
      </div>

      {/* Tracking details */}
      <div className="px-4 pb-3 space-y-2">
        {/* Carrier */}
        <div className="flex items-center gap-2 text-sm">
          <Truck className="w-4 h-4 text-muted-foreground" />
          <span>{tracking.carrier}</span>
          <span className="text-muted-foreground">#{tracking.tracking_number.slice(-8)}</span>
        </div>

        {/* Location */}
        {tracking.current_location && (
          <div className="flex items-center gap-2 text-sm">
            <MapPin className="w-4 h-4 text-muted-foreground" />
            <span>{tracking.current_location}</span>
          </div>
        )}

        {/* Delivery time */}
        {deliveryTime && (
          <div className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <span className="font-medium">{deliveryTime}</span>
          </div>
        )}

        {/* Delay reason */}
        {tracking.delay_reason && (
          <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 p-2 rounded-lg">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{tracking.delay_reason}</span>
          </div>
        )}
      </div>

      {/* Timeline (if events exist) */}
      {tracking.events && tracking.events.length > 0 && (
        <div className="px-4 pb-3">
          <details className="group">
            <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
              Toon tracking history ({tracking.events.length} updates)
            </summary>
            <div className="mt-2 space-y-2 pl-2 border-l-2 border-primary/20">
              {tracking.events.slice().reverse().slice(0, 4).map((event, idx) => {
                const dt = new Date(event.timestamp);
                return (
                  <div key={idx} className="pl-3 text-xs">
                    <div className="font-medium">{event.location}</div>
                    <div className="text-muted-foreground">
                      {dt.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })} {dt.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                );
              })}
            </div>
          </details>
        </div>
      )}

      {/* Track & Trace button */}
      {tracking.tracking_url && (
        <div className="px-4 pb-4">
          <a
            href={tracking.tracking_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-2 px-4 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Track & Trace
          </a>
        </div>
      )}
    </div>
  );
}
